import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, UserCheck, UserX, Clock, Shield, 
  CheckCircle, XCircle, MoreVertical, ArrowLeft,
  Search, Crown, Edit, Key, Mail, User as UserIcon
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_PERMISSIONS = {
  blog: { view: false, create: false, edit: false, delete: false, approve: false, manageCategories: false },
  portfolio: { view: false, create: false, edit: false, delete: false },
  products: { view: false, create: false, edit: false, delete: false },
  users: { view: false, approve: false, edit: false, delete: false }
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  
  const [users, setUsers] = useState([]);
  const [tools, setTools] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, active: 0, suspended: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form data for editing
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    newPassword: '',
    role: 'user',
    status: 'active',
    allowedTools: [],
    permissions: DEFAULT_PERMISSIONS,
    adminNotes: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, isAdmin]);

  const fetchData = async () => {
    try {
      const [usersRes, toolsRes, statsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/tools`),
        axios.get(`${API}/users/stats`)
      ]);
      setUsers(usersRes.data);
      setTools(toolsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load users data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesFilter = filter === 'all' || u.status === filter;
    const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const openEditModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      newPassword: '',
      role: userToEdit.role || 'user',
      status: userToEdit.status || 'active',
      allowedTools: userToEdit.allowedTools || [],
      permissions: userToEdit.permissions || DEFAULT_PERMISSIONS,
      adminNotes: userToEdit.adminNotes || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        status: formData.status,
        allowedTools: formData.allowedTools,
        permissions: formData.permissions,
        adminNotes: formData.adminNotes
      };

      // Only include password if it's been changed and user is super_admin
      if (formData.newPassword && isSuperAdmin) {
        updateData.password = formData.newPassword;
      }

      await axios.put(`${API}/users/${selectedUser._id}`, updateData);
      
      toast({
        title: "User Updated",
        description: `${formData.name}'s profile has been updated successfully.`
      });
      
      setEditModalOpen(false);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to update user",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (userToApprove) => {
    openEditModal(userToApprove);
  };

  const handleReject = async (userToReject) => {
    if (!confirm(`Are you sure you want to reject ${userToReject.name}?`)) return;
    try {
      await axios.put(`${API}/users/${userToReject._id}/reject`, {
        adminNotes: 'Registration rejected'
      });
      toast({
        title: "User Rejected",
        description: `${userToReject.name}'s registration has been rejected.`
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to reject user",
        variant: "destructive"
      });
    }
  };

  const handleQuickStatusChange = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/users/${userId}/status`, { status: newStatus });
      toast({
        title: "Status Updated",
        description: `User status changed to ${newStatus}.`
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!confirm(`Are you sure you want to DELETE ${userToDelete.name}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${userToDelete._id}`);
      toast({
        title: "User Deleted",
        description: `${userToDelete.name} has been deleted.`
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  const toggleTool = (toolSlug) => {
    if (toolSlug === 'all') {
      setFormData(prev => ({
        ...prev,
        allowedTools: prev.allowedTools.includes('all') ? [] : ['all']
      }));
    } else {
      setFormData(prev => {
        if (prev.allowedTools.includes('all')) {
          const allToolSlugs = tools.map(t => t.slug).filter(s => s !== toolSlug);
          return { ...prev, allowedTools: allToolSlugs };
        } else if (prev.allowedTools.includes(toolSlug)) {
          return { ...prev, allowedTools: prev.allowedTools.filter(s => s !== toolSlug) };
        } else {
          return { ...prev, allowedTools: [...prev.allowedTools, toolSlug] };
        }
      });
    }
  };

  const togglePermission = (feature, action) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [feature]: {
          ...prev.permissions[feature],
          [action]: !prev.permissions[feature]?.[action]
        }
      }
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500', icon: Clock, text: 'Pending' },
      active: { color: 'bg-green-500', icon: CheckCircle, text: 'Active' },
      suspended: { color: 'bg-red-500', icon: XCircle, text: 'Suspended' },
      rejected: { color: 'bg-gray-500', icon: XCircle, text: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon size={12} className="mr-1" />
        {config.text}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    if (role === 'super_admin') {
      return (
        <Badge className="bg-yellow-500 text-black text-xs">
          <Crown size={10} className="mr-1" />
          Super Admin
        </Badge>
      );
    }
    if (role === 'admin') {
      return (
        <Badge className="bg-purple-500 text-white text-xs">
          <Shield size={10} className="mr-1" />
          Admin
        </Badge>
      );
    }
    return null;
  };

  if (!user || !isAdmin) return null;

  return (
    <div className="bg-black text-white pt-24 min-h-screen">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
            >
              <ArrowLeft size={18} className="mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold">User Management</h1>
              <p className="text-gray-400">
                {isSuperAdmin ? 'Full control over all users' : 'Manage and assign permissions to users'}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white/10 border-white/20 cursor-pointer" onClick={() => setFilter('all')}>
            <CardContent className="p-4 text-center">
              <Users className="mx-auto text-[#F4B400] mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-gray-400">Total Users</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/20 border-yellow-500/40 cursor-pointer" onClick={() => setFilter('pending')}>
            <CardContent className="p-4 text-center">
              <Clock className="mx-auto text-yellow-500 mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-xs text-gray-400">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/20 border-green-500/40 cursor-pointer" onClick={() => setFilter('active')}>
            <CardContent className="p-4 text-center">
              <UserCheck className="mx-auto text-green-500 mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.active}</p>
              <p className="text-xs text-gray-400">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/20 border-red-500/40 cursor-pointer" onClick={() => setFilter('suspended')}>
            <CardContent className="p-4 text-center">
              <UserX className="mx-auto text-red-500 mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.suspended}</p>
              <p className="text-xs text-gray-400">Suspended</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-500/20 border-gray-500/40 cursor-pointer" onClick={() => setFilter('rejected')}>
            <CardContent className="p-4 text-center">
              <XCircle className="mx-auto text-gray-500 mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.rejected}</p>
              <p className="text-xs text-gray-400">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'active', 'suspended', 'rejected'].map((status) => (
              <Button
                key={status}
                onClick={() => setFilter(status)}
                variant={filter === status ? 'default' : 'outline'}
                size="sm"
                className={filter === status 
                  ? 'bg-[#F4B400] text-black' 
                  : 'border-white/20 text-white hover:bg-white/10'
                }
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
            <p className="text-gray-400">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Users className="mx-auto text-gray-500 mb-4" size={48} />
              <p className="text-gray-400">No users found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredUsers.map((u) => (
              <Card key={u._id} className="bg-white text-black hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#F4B400] rounded-full flex items-center justify-center text-black font-bold text-lg">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{u.name}</h3>
                          {getRoleBadge(u.role)}
                          {getStatusBadge(u.status)}
                        </div>
                        <p className="text-sm text-gray-500">{u.email}</p>
                        <p className="text-xs text-gray-400">
                          Registered: {new Date(u.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Tool access badges */}
                      <div className="hidden md:flex gap-1">
                        {u.allowedTools?.includes('all') ? (
                          <Badge variant="outline" className="text-xs">All Tools</Badge>
                        ) : u.allowedTools?.length > 0 ? (
                          <Badge variant="outline" className="text-xs">
                            {u.allowedTools.length} tool(s)
                          </Badge>
                        ) : u.status === 'active' && (
                          <Badge variant="outline" className="text-xs text-gray-400">No tools</Badge>
                        )}
                      </div>

                      {/* Actions */}
                      {u.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApprove(u)}
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReject(u)}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <XCircle size={14} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}

                      {u.status !== 'pending' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(u)}>
                              <Edit size={14} className="mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            {u.status === 'active' && (
                              <DropdownMenuItem onClick={() => handleQuickStatusChange(u._id, 'suspended')}>
                                <UserX size={14} className="mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            )}
                            {u.status === 'suspended' && (
                              <DropdownMenuItem onClick={() => handleQuickStatusChange(u._id, 'active')}>
                                <UserCheck size={14} className="mr-2" />
                                Reactivate
                              </DropdownMenuItem>
                            )}
                            {isSuperAdmin && u.role !== 'super_admin' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteUser(u)}
                                  className="text-red-600"
                                >
                                  <XCircle size={14} className="mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-white text-black max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={20} />
              {selectedUser?.status === 'pending' ? 'Approve & Configure User' : 'Edit User'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="access">Access & Tools</TabsTrigger>
                <TabsTrigger value="permissions">Permissions</TabsTrigger>
              </TabsList>

              {/* Profile Tab */}
              <TabsContent value="profile" className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="edit-name">Full Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-email">Email Address</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2"
                  />
                </div>

                {isSuperAdmin && (
                  <div>
                    <Label htmlFor="edit-password" className="flex items-center gap-2">
                      <Key size={14} />
                      New Password (leave empty to keep current)
                    </Label>
                    <Input
                      id="edit-password"
                      type="password"
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      placeholder="Enter new password..."
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Only Super Admin can change user passwords.</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Role</Label>
                    <Select 
                      value={formData.role} 
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="admin-notes">Admin Notes</Label>
                  <Textarea
                    id="admin-notes"
                    value={formData.adminNotes}
                    onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
                    placeholder="Internal notes about this user..."
                    className="mt-2"
                    rows={2}
                  />
                </div>
              </TabsContent>

              {/* Access & Tools Tab */}
              <TabsContent value="access" className="space-y-4 mt-4">
                <div>
                  <Label className="font-semibold mb-2 block">Tool Access</Label>
                  <p className="text-sm text-gray-500 mb-3">Select which tools this user can access</p>
                  <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
                    <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded border-b">
                      <Checkbox
                        id="tool-all"
                        checked={formData.allowedTools.includes('all')}
                        onCheckedChange={() => toggleTool('all')}
                      />
                      <label htmlFor="tool-all" className="flex-1 cursor-pointer font-semibold text-[#F4B400]">
                        Full Access (All Tools)
                      </label>
                    </div>
                    {tools.filter(t => t.status === 'active').map((tool) => (
                      <div key={tool._id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                        <Checkbox
                          id={`tool-${tool.slug}`}
                          checked={formData.allowedTools.includes('all') || formData.allowedTools.includes(tool.slug)}
                          onCheckedChange={() => toggleTool(tool.slug)}
                          disabled={formData.allowedTools.includes('all')}
                        />
                        <label htmlFor={`tool-${tool.slug}`} className="flex-1 cursor-pointer">
                          <span className="font-medium">{tool.name}</span>
                          <span className="text-xs text-gray-500 block">{tool.description}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Permissions Tab */}
              <TabsContent value="permissions" className="space-y-4 mt-4">
                {formData.role === 'user' ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500">Set granular permissions for this user. Admins have all permissions automatically.</p>
                    
                    {/* Blog Permissions */}
                    <div className="border rounded-lg p-4">
                      <p className="font-semibold mb-3">Blog</p>
                      <div className="grid grid-cols-3 gap-2">
                        {['view', 'create', 'edit', 'delete', 'approve', 'manageCategories'].map((action) => (
                          <div key={action} className="flex items-center gap-1">
                            <Checkbox
                              id={`blog-${action}`}
                              checked={formData.permissions.blog?.[action] || false}
                              onCheckedChange={() => togglePermission('blog', action)}
                            />
                            <label htmlFor={`blog-${action}`} className="text-sm cursor-pointer">
                              {action === 'manageCategories' ? 'Categories' : action.charAt(0).toUpperCase() + action.slice(1)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Portfolio Permissions */}
                    <div className="border rounded-lg p-4">
                      <p className="font-semibold mb-3">Portfolio</p>
                      <div className="grid grid-cols-4 gap-2">
                        {['view', 'create', 'edit', 'delete'].map((action) => (
                          <div key={action} className="flex items-center gap-1">
                            <Checkbox
                              id={`portfolio-${action}`}
                              checked={formData.permissions.portfolio?.[action] || false}
                              onCheckedChange={() => togglePermission('portfolio', action)}
                            />
                            <label htmlFor={`portfolio-${action}`} className="text-sm cursor-pointer capitalize">
                              {action}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Products Permissions */}
                    <div className="border rounded-lg p-4">
                      <p className="font-semibold mb-3">Products</p>
                      <div className="grid grid-cols-4 gap-2">
                        {['view', 'create', 'edit', 'delete'].map((action) => (
                          <div key={action} className="flex items-center gap-1">
                            <Checkbox
                              id={`products-${action}`}
                              checked={formData.permissions.products?.[action] || false}
                              onCheckedChange={() => togglePermission('products', action)}
                            />
                            <label htmlFor={`products-${action}`} className="text-sm cursor-pointer capitalize">
                              {action}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Users Permissions */}
                    <div className="border rounded-lg p-4">
                      <p className="font-semibold mb-3">Users</p>
                      <div className="grid grid-cols-4 gap-2">
                        {['view', 'approve', 'edit', 'delete'].map((action) => (
                          <div key={action} className="flex items-center gap-1">
                            <Checkbox
                              id={`users-${action}`}
                              checked={formData.permissions.users?.[action] || false}
                              onCheckedChange={() => togglePermission('users', action)}
                            />
                            <label htmlFor={`users-${action}`} className="text-sm cursor-pointer capitalize">
                              {action}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Shield className="mx-auto text-purple-500 mb-3" size={48} />
                    <p className="font-semibold">Admin/Super Admin Role</p>
                    <p className="text-sm text-gray-500">This user has full access to all features.</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser} 
              disabled={actionLoading}
              className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
            >
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
